import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ClientLayout from "../components/ClientLayout.jsx";
import { ListSectionSkeleton } from "../components/loading/SkeletonBlocks.jsx";
import { getClientPrograms, mergeClientProgramsWithOwnedListings } from "../lib/appwrite.js";
import { fetchOwnedListingsViaGateway } from "../lib/stripe.js";
import { useAuthUserId } from "../hooks/useAuthUserId.js";

const DEFAULT_COVER =
    "linear-gradient(135deg, rgba(255,118,25,0.55) 0%, rgba(245,158,11,0.45) 100%)";

function formatDate(isoString) {
    return new Date(isoString).toLocaleDateString("en-CA", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function ProgramRow({ program }) {
    const dateLabel = program.dateLabel ?? "Assigned";
    const dateValue = program.dateValue ?? program.assignedAt ?? program.lastActiveAt;

    return (
        <li className="px-5 py-4">
            <div className="flex items-center gap-4">
                <div
                    className="size-10 shrink-0 rounded-[10px]"
                    style={{ background: DEFAULT_COVER }}
                />
                <div>
                    <Link
                        to={`/programs/${program.id}`}
                        className="text-sm font-medium tracking-[-0.15px] text-hc-text hover:text-hc-primary transition"
                    >
                        {program.title}
                    </Link>
                    <p className="text-xs text-hc-text-dim">
                        {program.coachName && (
                            <>
                                by {program.coachName}
                                <span className="mx-1.5 opacity-40">·</span>
                            </>
                        )}
                        {dateValue
                            ? `${dateLabel} ${formatDate(dateValue)}`
                            : "No activity recorded yet"}
                    </p>
                </div>
            </div>
        </li>
    );
}

function ProgramSection({ title, programs, emptyMessage }) {
    return (
        <section className="overflow-hidden rounded-2xl border border-hc-surface-border bg-hc-surface">
            <div className="border-b border-hc-surface-border px-5 py-3.5">
                <h2 className="text-sm font-semibold tracking-[-0.15px] text-hc-text">
                    {title}
                    <span className="ml-2 rounded-full border border-hc-surface-border bg-hc-surface-strong px-2 py-0.5 text-xs font-medium text-hc-text-muted">
                        {programs.length}
                    </span>
                </h2>
            </div>
            {programs.length === 0 ? (
                <p className="px-5 py-8 text-center text-sm text-hc-text-dim">{emptyMessage}</p>
            ) : (
                <ul className="divide-y divide-hc-surface-border/80">
                    {programs.map((program) => (
                        <ProgramRow
                            key={program.id}
                            program={program}
                        />
                    ))}
                </ul>
            )}
        </section>
    );
}

export default function ClientProgramsPage() {
    const { userId } = useAuthUserId();
    const [programs, setPrograms] = useState({ current: [], previous: [] });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) return;

        let cancelled = false;

        (async () => {
            try {
                const [base, owned] = await Promise.all([
                    getClientPrograms(userId),
                    fetchOwnedListingsViaGateway().catch(() => ({ listings: [] })),
                ]);
                const result = mergeClientProgramsWithOwnedListings(base, owned.listings ?? []);
                if (!cancelled) setPrograms(result);
            } catch (err) {
                if (!cancelled) setError(err?.message ?? "Failed to load your programs.");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();

        return () => { cancelled = true; };
    }, [userId]);

    const hasPrograms = programs.current.length > 0 || programs.previous.length > 0;

    return (
        <ClientLayout>
            {isLoading ? (
                <ListSectionSkeleton rows={3} />
            ) : error ? (
                <div className="rounded-2xl border border-hc-error/30 bg-hc-error/10 px-6 py-8 text-center">
                    <p className="text-sm font-semibold text-hc-text">Could not load your programs</p>
                    <p className="mt-1.5 text-xs text-hc-error">{error}</p>
                </div>
            ) : !hasPrograms ? (
                <div className="rounded-2xl border border-hc-surface-border bg-hc-surface px-6 py-14 text-center">
                    <div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full border border-hc-surface-border-strong bg-hc-surface-strong">
                        <i className="icon-collection text-xl leading-none text-hc-text-dim" aria-hidden="true" />
                    </div>
                    <p className="font-semibold text-hc-text">No programs yet</p>
                    <p className="mt-1.5 max-w-xs mx-auto text-sm text-hc-text-dim">
                        When your coach assigns a program to you, it will appear here.
                    </p>
                </div>
            ) : (
                <div className="space-y-5">
                    <ProgramSection
                        title="Current programs"
                        programs={programs.current}
                        emptyMessage="Your coach has not assigned a program yet."
                    />
                    <ProgramSection
                        title="Previous programs"
                        programs={programs.previous}
                        emptyMessage="Programs you have completed or moved on from will appear here."
                    />
                </div>
            )}
        </ClientLayout>
    );
}
